import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build";
import "../../scss/ckeditor.scss";
import { ThemeProvider, StyledEngineProvider, useTheme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { visionURL } from "../../config/environment";

const useStyles = makeStyles(theme => ({
  "@global": {
    ".ck-dropdown__panel .ck-button_with-text:hover .ck-button__icon": {
      color: theme.palette.primary.main
    },
    ".ck.ck-icon": {
      color: theme.palette.action.active
    },
    ".ck.ck-button:not(.ck-disabled):hover, .ck.ck-list__item .ck-button:hover:not(.ck-disabled)": {
      backgroundColor: theme.palette.action.hover
    }
  }
}));

const PolicyEditor = ({
  onSave,
  initialData,
  onReady,
  trainingLinkComponent
}) => {
  const [layout, setLayout] = useState(false);
  const presenceListElementRef = useRef(null);
  const sidebarElementRef = useRef(null);
  const theme = useTheme();

  /**
   * Function used to render react components as widgets in the editor.
   * This method wraps the rendered widget with the Material UI theme so widgets are
   * styled correctly.
   */
  const renderWidget = component => (props, domElement) => {
    const widgetComponent = (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>{component(props)}</ThemeProvider>
      </StyledEngineProvider>
    );
    ReactDOM.render(widgetComponent, domElement);
  };

  useStyles();

  const fetchToken = async () => {
    const response = await fetch(
      `${visionURL}/admin_console/api/ckeditor_tokens`,
      {
        credentials: "include"
      }
    ).then(response => response.json());
    return response.token;
  };

  useEffect(() => {
    setLayout(true);
  }, []);

  const handleEditorReady = editor => {
    window.addEventListener("resize", refreshDisplayMode);
    window.addEventListener("beforeunload", checkPendingActions);
    refreshDisplayMode(editor);
    onReady && onReady(editor);
  };

  const renderEditor = () => {
    return (
      <div className="row row-editor">
        {layout && (
          <CKEditor
            onReady={handleEditorReady}
            onChange={(event, editor) => {}}
            editor={Editor}
            config={{
              autosave: {
                save(editor) {
                  onSave && onSave(editor.getData());
                }
              },
              initialData,
              toolbar: [
                "InsertDropDown",
                "|",
                "heading",
                "|",
                "fontsize",
                "fontfamily",
                "|",
                "bold",
                "italic",
                "underline",
                "strikethrough",
                "removeFormat",
                "highlight",
                "|",
                "alignment",
                "|",
                "numberedList",
                "bulletedList",
                "|",
                "blockquote",
                "insertTable",
                "|",
                "undo",
                "redo",
                "|",
                "comment",
                "|",
                "trackChanges"
              ],
              cloudServices: {
                tokenUrl: fetchToken,
                uploadUrl: "https://88219.cke-cs.com/easyimage/upload/",
                webSocketUrl: "wss://88219.cke-cs.com/ws"
              },
              collaboration: { channelId: "asdfasdfasdf" },
              image: {
                toolbar: [
                  "imageStyle:inline",
                  "imageStyle:block",
                  "imageStyle:side",
                  "|",
                  "toggleImageCaption",
                  "imageTextAlternative",
                  "|",
                  "comment"
                ]
              },
              table: {
                contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
                tableToolbar: ["comment"]
              },
              mediaEmbed: { toolbar: ["comment"] },
              sidebar: { container: sidebarElementRef.current },
              presenceList: { container: presenceListElementRef.current },
              trainingLinks: {
                trainingLinkRenderer: renderWidget(trainingLinkComponent)
              }
            }}
            data=""
          />
        )}
        <div ref={sidebarElementRef} className="sidebar"></div>
      </div>
    );
  };

  const refreshDisplayMode = editor => {};
  const checkPendingActions = (editor, domEvt) => {
    if (editor.plugins.get("PendingActions").hasAny) {
      domEvt.preventDefault();
      domEvt.returnValue = true;
    }
  };
  return (
    <div className="App">
      <main>
        <div id="my-container"></div>
        <div className="centered">
          <div className="row-presence">
            <div ref={presenceListElementRef} className="presence"></div>
          </div>
          {renderEditor()}
        </div>
      </main>
      <div className="revision-viewer-container">
        <div className="editor-container">
          <div className="revision-viewer-editor"></div>
          <div className="revision-viewer-sidebar"></div>
        </div>
      </div>
    </div>
  );
};

export default PolicyEditor;

PolicyEditor.propTypes = {
  onSave: PropTypes.func,
  initialData: PropTypes.string,
  trainingLinkComponent: PropTypes.func
};
